import { useCallback, useState } from "react";

export const useVariantSelection = (leafItemIds: string[]) => {
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    const isAllSelected = leafItemIds.length > 0 && leafItemIds.every((id) => selectedItems.has(id));
    const isIndeterminate = !isAllSelected && leafItemIds.some((id) => selectedItems.has(id));

    const toggleSelectAll = useCallback(() => {
        setSelectedItems((previous) => {
            const allSelected = leafItemIds.length > 0 && leafItemIds.every((id) => previous.has(id));
            return allSelected ? new Set() : new Set(leafItemIds);
        });
    }, [leafItemIds]);

    const toggleSelectItem = useCallback((itemId: string) => {
        setSelectedItems((previous) => {
            const next = new Set(previous);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    }, []);

    const toggleGroupItems = useCallback((leafIds: string[]) => {
        setSelectedItems((previous) => {
            if (leafIds.length === 0) return previous;
            const allSelected = leafIds.every((id) => previous.has(id));
            const next = new Set(previous);
            if (allSelected) {
                for (const id of leafIds) next.delete(id);
            } else {
                for (const id of leafIds) next.add(id);
            }
            return next;
        });
    }, []);

    const getSelectedLeafIds = useCallback(
        (catalogItems: { isLeaf?: boolean; id?: string | null }[]) => {
            return catalogItems
                .filter((item) => item.isLeaf && Boolean(item.id) && selectedItems.has(item.id as string))
                .map((item) => item.id as string);
        },
        [selectedItems],
    );

    const reset = useCallback(() => setSelectedItems(new Set()), []);

    return {
        items: selectedItems,
        isAllSelected,
        isIndeterminate,
        toggleSelectAll,
        toggleSelectItem,
        toggleGroupItems,
        getSelectedLeafIds,
        reset,
    };
};
